import React, { useEffect, useMemo, useState } from "react";
import API from "../../api";
import { useDispatch, useSelector } from "react-redux";
import { selectUser, setUser } from "../../store/authSlice";
import "../css/adminResetPassword.css";

function sanitizeRole(r) {
  return String(r || "").trim().toLowerCase();
}

const ROLE_OPTIONS = [
  { label: "Admin", value: "admin" },
  { label: "Sub-Admin", value: "sub-admin" },
  { label: "Agent", value: "agent" },
];

const ASS_OPTIONS = [
  { label: "Admin", value: 0 },
  { label: "Alan", value: 1 },
  { label: "Chris", value: 2 },
  { label: "David", value: 3 },
  { label: "akshay", value: 4 },
  { label: "Mike", value: 5 },
  { label: "cyrus", value: 6 },
];

export default function AdminResetPassword() {
  const dispatch = useDispatch();
  const user = useSelector(selectUser);

  const [loadingMe, setLoadingMe] = useState(false);

  const role = useMemo(() => sanitizeRole(user?.role), [user]);
  const isAdmin = role === "admin";

  // Users table
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");

  // Search
  const [q, setQ] = useState("");

  // RESET PASSWORD MODAL
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [pw, setPw] = useState("");
  const [cpw, setCpw] = useState("");
  const [saveLoading, setSaveLoading] = useState(false);
  const [saveErr, setSaveErr] = useState("");
  const [saveOk, setSaveOk] = useState("");

  // EDIT USER MODAL
  const [editOpen, setEditOpen] = useState(false);
  const [editUser, setEditUser] = useState(null);
  const [editForm, setEditForm] = useState({
    username: "",
    email: "",
    mobile: "",
    status: "",
    photo_url: "",
    role: "agent",
    ass: 0,
  });
  const [editLoading, setEditLoading] = useState(false);
  const [editErr, setEditErr] = useState("");
  const [editOk, setEditOk] = useState("");

  // ✅ CREATE USER (INVITE) MODAL - NO PASSWORD FIELDS
  const [createOpen, setCreateOpen] = useState(false);
  const [createForm, setCreateForm] = useState({
    username: "",
    email: "",
    mobile: "",
    role: "agent",
    ass: 0,
    status: "Active now",
    photo_url: "",
  });
  const [createLoading, setCreateLoading] = useState(false);
  const [createErr, setCreateErr] = useState("");
  const [createOk, setCreateOk] = useState("");

  // DELETE USER MODAL
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleteUser, setDeleteUser] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [deleteErr, setDeleteErr] = useState("");


  useEffect(() => {
    API.defaults.withCredentials = true;
  }, []);

  // Load current user
  useEffect(() => {
    let ignore = false;
    (async () => {
      if (user?.id) return;
      try {
        setLoadingMe(true);
        const { data } = await API.get("/auth/me");
        if (!ignore && data?.user) dispatch(setUser(data.user));
      } catch (_) {
        // ignore
      } finally {
        if (!ignore) setLoadingMe(false);
      }
    })();
    return () => {
      ignore = true;
    };
  }, [user, dispatch]);

  const fetchUsers = async () => {
    const resp = await API.get("/auth/admin/users-redux", {
      params: { requesterId: user?.id },
    });
    return Array.isArray(resp?.data) ? resp.data : resp?.data?.users || [];
  };

  useEffect(() => {
    let ignore = false;
    (async () => {
      if (!isAdmin) return;
      try {
        setLoading(true);
        setErr("");
        const list = await fetchUsers();
        if (!ignore) setRows(list);
      } catch (e) {
        console.error(e);
        if (!ignore) setErr("Could not load users.");
      } finally {
        if (!ignore) setLoading(false);
      }
    })();
    return () => {
      ignore = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAdmin]);

  const filteredRows = useMemo(() => {
    const s = String(q || "").trim().toLowerCase();
    if (!s) return rows;

    return rows.filter((u) => {
      const username = String(u.username || "").toLowerCase();
      const email = String(u.email || "").toLowerCase();
      const role = String(u.role || "").toLowerCase();
      const mobile = String(u.mobile || "").toLowerCase();
      const status = String(u.status || "").toLowerCase();
      return (
        username.includes(s) ||
        email.includes(s) ||
        role.includes(s) ||
        mobile.includes(s) ||
        status.includes(s)
      );
    });
  }, [rows, q]);

  // -------------------- RESET PASSWORD --------------------
  const openReset = (u) => {
    setSelectedUser(u);
    setPw("");
    setCpw("");
    setSaveErr("");
    setSaveOk("");
    setModalOpen(true);
  };

  const closeReset = () => {
    setModalOpen(false);
    setSelectedUser(null);
    setPw("");
    setCpw("");
    setSaveErr("");
    setSaveOk("");
  };

  const validateReset = () => {
    const p = String(pw || "");
    const c = String(cpw || "");
    if (!p || !c) return "Password and confirm password are required.";
    if (p.length < 6) return "Password must be at least 6 characters.";
    if (p !== c) return "Passwords do not match.";
    return "";
  };

  const onSaveReset = async () => {
    const msg = validateReset();
    if (msg) return setSaveErr(msg);
    if (!selectedUser?.id) return setSaveErr("Missing user.");

    setSaveLoading(true);
    setSaveErr("");
    setSaveOk("");

    try {
      await API.post("/auth/admin/reset-password-redux", {
        requesterId: user.id,
        userId: selectedUser.id,
        password: pw,
        confirmPassword: cpw,
      });

      setSaveOk("Password reset successfully.");
      setTimeout(closeReset, 700);
    } catch (e) {
      console.error(e);
      setSaveErr(
        e?.response?.data?.error ||
        e?.response?.data?.message ||
        "Reset failed."
      );
    } finally {
      setSaveLoading(false);
    }
  };

  // -------------------- EDIT USER --------------------
  const openEdit = (u) => {
    setEditUser(u);
    setEditErr("");
    setEditOk("");
    setEditForm({
      username: u?.username || "",
      email: u?.email || "",
      mobile: u?.mobile || "",
      status: u?.status || "",
      photo_url: u?.photo_url || "",
      role: (u?.role || "agent").toString(),
      ass:
        u?.ass === null || u?.ass === undefined || u?.ass === ""
          ? 0
          : Number(u.ass),
    });
    setEditOpen(true);
  };

  const closeEdit = () => {
    setEditOpen(false);
    setEditUser(null);
    setEditErr("");
    setEditOk("");
  };

  const validateEdit = () => {
    if (!editUser?.id) return "Missing user.";
    if (!String(editForm.username || "").trim()) return "Username is required.";
    if (!String(editForm.email || "").trim()) return "Email is required.";
    if (!String(editForm.mobile || "").trim()) return "Mobile is required.";
    if (!String(editForm.role || "").trim()) return "Role is required.";
    if (!Number.isFinite(Number(editForm.ass))) return "ASS must be a number.";
    return "";
  };

  const onSaveEdit = async () => {
    const msg = validateEdit();
    if (msg) return setEditErr(msg);

    setEditLoading(true);
    setEditErr("");
    setEditOk("");

    try {
      await API.post("/auth/admin/update-user-redux", {
        requesterId: user.id,
        userId: editUser.id,
        username: editForm.username,
        email: editForm.email,
        mobile: editForm.mobile,
        status: editForm.status,
        photo_url: editForm.photo_url,
        role: editForm.role,
        ass: Number(editForm.ass),
      });

      setEditOk("User updated successfully.");
      const list = await fetchUsers();
      setRows(list);

      setTimeout(closeEdit, 700);
    } catch (e) {
      console.error(e);
      setEditErr(
        e?.response?.data?.error ||
        e?.response?.data?.message ||
        "Update failed."
      );
    } finally {
      setEditLoading(false);
    }
  };

  // -------------------- CREATE USER (INVITE) --------------------
  const openCreate = () => {
    setCreateErr("");
    setCreateOk("");
    setCreateForm({
      username: "",
      email: "",
      mobile: "",
      role: "agent",
      ass: 0,
      status: "Active now",
      photo_url: "",
    });
    setCreateOpen(true);
  };

  const closeCreate = () => {
    setCreateOpen(false);
    setCreateErr("");
    setCreateOk("");
  };

  const validateCreate = () => {
    if (!String(createForm.username || "").trim()) return "Username is required.";
    if (!String(createForm.email || "").trim()) return "Email is required.";
    if (!String(createForm.mobile || "").trim()) return "Mobile is required.";
    if (!String(createForm.role || "").trim()) return "Role is required.";
    if (!Number.isFinite(Number(createForm.ass))) return "ASS is invalid.";
    return "";
  };

  const onSaveCreate = async () => {
    const msg = validateCreate();
    if (msg) return setCreateErr(msg);

    setCreateLoading(true);
    setCreateErr("");
    setCreateOk("");

    try {
      const resp = await API.post("/auth/admin/create-user-invite-redux", {
        requesterId: user.id,
        username: createForm.username.trim(),
        email: createForm.email.trim().toLowerCase(),
        mobile: createForm.mobile.trim(),
        role: createForm.role,
        ass: Number(createForm.ass),
        status: createForm.status || "Active now",
        photo_url: createForm.photo_url || "",
      });

      const link = resp?.data?.setPasswordLink;

      setCreateOk(
        link
          ? `User created. Email sent. (Link: ${link})`
          : "User created and email sent to set password."
      );

      const list = await fetchUsers();
      setRows(list);

      setTimeout(closeCreate, 900);
    } catch (e) {
      console.error(e);
      setCreateErr(
        e?.response?.data?.error ||
        e?.response?.data?.message ||
        "Create user failed."
      );
    } finally {
      setCreateLoading(false);
    }
  };

  // Auth states
  if (loadingMe && !user?.id) {
    return (
      <div className="arp-wrap">
        <div className="arp-card">Loading…</div>
      </div>
    );
  }

  if (!isAdmin) {
    return (
      <div className="arp-wrap">
        <div className="arp-card arp-unauth">You are not authorized</div>
      </div>
    );
  }

  // -------------------- DELETE USER --------------------
  const openDelete = (u) => {
    setDeleteUser(u);
    setDeleteErr("");
    setDeleteOpen(true);
  };

  const closeDelete = () => {
    setDeleteOpen(false);
    setDeleteUser(null);
    setDeleteErr("");
  };

  const onConfirmDelete = async () => {
    if (!deleteUser?.id) return;

    setDeleteLoading(true);
    setDeleteErr("");

    try {
      await API.post("/auth/admin/delete-user-redux", {
        requesterId: user.id,
        userId: deleteUser.id,
      });

      // remove user from UI instantly
      setRows((prev) => prev.filter((r) => r.id !== deleteUser.id));

      closeDelete();
    } catch (e) {
      console.error(e);
      setDeleteErr(
        e?.response?.data?.error ||
        e?.response?.data?.message ||
        "Delete failed."
      );
    } finally {
      setDeleteLoading(false);
    }
  };


  return (
    <div className="arp-wrap">
      <div className="arp-header">
        <h2 className="arp-title">Admin Users</h2>

        <div className="arp-controls">
          <input
            className="arp-search"
            placeholder="Search username / email / role / mobile / status"
            value={q}
            onChange={(e) => setQ(e.target.value)}
          />

          <button className="arp-btn primary" onClick={openCreate}>
            + New User
          </button>
        </div>
      </div>

      <div className="arp-card">
        {err && <div className="arp-error">{err}</div>}
        {loading && <div className="arp-loading">Loading users…</div>}

        {!loading && !err && (
          <div className="arp-table-wrap">
            <table className="arp-table">
              <thead>
                <tr>
                  <th>ID</th>
                  <th>Username</th>
                  <th>Email</th>
                  <th>Mobile</th>
                  <th>Role</th>
                  <th>ASS</th>
                  <th>Status</th>
                  <th style={{ width: 240 }}>Action</th>
                </tr>
              </thead>
              <tbody>
                {filteredRows.length === 0 ? (
                  <tr>
                    <td colSpan="8" style={{ textAlign: "center", padding: 16 }}>
                      No users found.
                    </td>
                  </tr>
                ) : (
                  filteredRows.map((u) => (
                    <tr key={u.id}>
                      <td>{u.id}</td>
                      <td>{u.username || "—"}</td>
                      <td>{u.email || "—"}</td>
                      <td>{u.mobile || "—"}</td>
                      <td>{u.role || "—"}</td>
                      <td>{u.ass ?? "—"}</td>
                      <td>{u.status || "—"}</td>
                      <td className="arp-actions-cell">
                        <button className="arp-btn" onClick={() => openEdit(u)}>
                          Edit
                        </button>
                        <button className="arp-btn" onClick={() => openReset(u)}>
                          Reset Password
                        </button>
                        <button
                          className="arp-btn danger"
                          onClick={() => openDelete(u)}
                        >
                          Delete
                        </button>
                      </td>

                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* RESET PASSWORD MODAL */}
      {modalOpen && (
        <div className="arp-backdrop" onClick={closeReset} role="presentation">
          <div className="arp-modal" onClick={(e) => e.stopPropagation()}>
            <div className="arp-modal-head">
              <div>
                <div className="arp-modal-title">Reset Password</div>
                <div className="arp-modal-sub">
                  User: <b>{selectedUser?.username || "—"}</b> (ID:{" "}
                  {selectedUser?.id})
                </div>
              </div>
              <button className="arp-x" onClick={closeReset} aria-label="Close">
                ✕
              </button>
            </div>

            <div className="arp-modal-body">
              {saveErr && <div className="arp-error">{saveErr}</div>}
              {saveOk && <div className="arp-ok">{saveOk}</div>}

              <div className="arp-field">
                <label>New Password</label>
                <input
                  type="password"
                  value={pw}
                  onChange={(e) => setPw(e.target.value)}
                  autoComplete="new-password"
                  placeholder="Enter new password"
                />
              </div>

              <div className="arp-field">
                <label>Confirm Password</label>
                <input
                  type="password"
                  value={cpw}
                  onChange={(e) => setCpw(e.target.value)}
                  autoComplete="new-password"
                  placeholder="Confirm new password"
                />
              </div>

              <div className="arp-actions">
                <button className="arp-btn ghost" onClick={closeReset}>
                  Cancel
                </button>
                <button
                  className="arp-btn primary"
                  onClick={onSaveReset}
                  disabled={saveLoading}
                >
                  {saveLoading ? "Saving…" : "Save"}
                </button>
              </div>

              <div className="arp-hint">Password must be at least 6 characters.</div>
            </div>
          </div>
        </div>
      )}

      {/* EDIT USER MODAL */}
      {editOpen && (
        <div className="arp-backdrop" onClick={closeEdit} role="presentation">
          <div
            className="arp-modal arp-modal-wide"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="arp-modal-head">
              <div>
                <div className="arp-modal-title">Edit User</div>
                <div className="arp-modal-sub">
                  Editing: <b>{editUser?.username || "—"}</b> (ID: {editUser?.id})
                </div>
              </div>
              <button className="arp-x" onClick={closeEdit} aria-label="Close">
                ✕
              </button>
            </div>

            <div className="arp-modal-body">
              {editErr && <div className="arp-error">{editErr}</div>}
              {editOk && <div className="arp-ok">{editOk}</div>}

              <div className="arp-form-grid">
                <div className="arp-field">
                  <label>Username *</label>
                  <input
                    value={editForm.username}
                    onChange={(e) =>
                      setEditForm((p) => ({ ...p, username: e.target.value }))
                    }
                    placeholder="Username"
                  />
                </div>

                <div className="arp-field">
                  <label>Mobile *</label>
                  <input
                    value={editForm.mobile}
                    onChange={(e) =>
                      setEditForm((p) => ({ ...p, mobile: e.target.value }))
                    }
                    placeholder="Mobile"
                  />
                </div>

                <div className="arp-field">
                  <label>Email *</label>
                  <input
                    value={editForm.email}
                    onChange={(e) =>
                      setEditForm((p) => ({ ...p, email: e.target.value }))
                    }
                    placeholder="Email"
                  />
                </div>

                <div className="arp-field">
                  <label>Status</label>
                  <input
                    value={editForm.status}
                    onChange={(e) =>
                      setEditForm((p) => ({ ...p, status: e.target.value }))
                    }
                    placeholder="Status"
                  />
                </div>

                <div className="arp-field">
                  <label>Role *</label>
                  <select
                    value={editForm.role}
                    onChange={(e) =>
                      setEditForm((p) => ({ ...p, role: e.target.value }))
                    }
                  >
                    {ROLE_OPTIONS.map((o) => (
                      <option key={o.value} value={o.value}>
                        {o.label}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="arp-field">
                  <label>ASS *</label>
                  <select
                    value={String(editForm.ass)}
                    onChange={(e) =>
                      setEditForm((p) => ({ ...p, ass: Number(e.target.value) }))
                    }
                  >
                    {ASS_OPTIONS.map((o) => (
                      <option key={o.value} value={o.value}>
                        {o.label} ({o.value})
                      </option>
                    ))}
                  </select>
                </div>

                <div className="arp-field arp-span-2">
                  <label>Photo URL</label>
                  <input
                    value={editForm.photo_url}
                    onChange={(e) =>
                      setEditForm((p) => ({ ...p, photo_url: e.target.value }))
                    }
                    placeholder="https://..."
                  />
                </div>
              </div>

              <div className="arp-actions arp-actions-bottom">
                <button className="arp-btn ghost" onClick={closeEdit}>
                  Cancel
                </button>
                <button
                  className="arp-btn primary"
                  onClick={onSaveEdit}
                  disabled={editLoading}
                >
                  {editLoading ? "Saving…" : "Save"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* CREATE USER INVITE MODAL */}
      {createOpen && (
        <div className="arp-backdrop" onClick={closeCreate} role="presentation">
          <div
            className="arp-modal arp-modal-wide"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="arp-modal-head">
              <div>
                <div className="arp-modal-title">Create New User</div>
                <div className="arp-modal-sub">
                  Password will be set by user via email link.
                </div>
              </div>
              <button className="arp-x" onClick={closeCreate} aria-label="Close">
                ✕
              </button>
            </div>

            <div className="arp-modal-body">
              {createErr && <div className="arp-error">{createErr}</div>}
              {createOk && <div className="arp-ok">{createOk}</div>}

              <div className="arp-form-grid">
                <div className="arp-field">
                  <label>Username *</label>
                  <input
                    value={createForm.username}
                    onChange={(e) =>
                      setCreateForm((p) => ({ ...p, username: e.target.value }))
                    }
                    placeholder="Username"
                  />
                </div>

                <div className="arp-field">
                  <label>Mobile *</label>
                  <input
                    value={createForm.mobile}
                    onChange={(e) =>
                      setCreateForm((p) => ({ ...p, mobile: e.target.value }))
                    }
                    placeholder="Mobile"
                  />
                </div>

                <div className="arp-field">
                  <label>Email *</label>
                  <input
                    value={createForm.email}
                    onChange={(e) =>
                      setCreateForm((p) => ({ ...p, email: e.target.value }))
                    }
                    placeholder="Email"
                  />
                </div>

                <div className="arp-field">
                  <label>Status</label>
                  <input
                    value={createForm.status}
                    onChange={(e) =>
                      setCreateForm((p) => ({ ...p, status: e.target.value }))
                    }
                    placeholder="Active now"
                  />
                </div>

                <div className="arp-field">
                  <label>Role *</label>
                  <select
                    value={createForm.role}
                    onChange={(e) =>
                      setCreateForm((p) => ({ ...p, role: e.target.value }))
                    }
                  >
                    {ROLE_OPTIONS.map((o) => (
                      <option key={o.value} value={o.value}>
                        {o.label}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="arp-field">
                  <label>ASS *</label>
                  <select
                    value={String(createForm.ass)}
                    onChange={(e) =>
                      setCreateForm((p) => ({ ...p, ass: Number(e.target.value) }))
                    }
                  >
                    {ASS_OPTIONS.map((o) => (
                      <option key={o.value} value={o.value}>
                        {o.label} ({o.value})
                      </option>
                    ))}
                  </select>
                </div>

                <div className="arp-field arp-span-2">
                  <label>Photo URL</label>
                  <input
                    value={createForm.photo_url}
                    onChange={(e) =>
                      setCreateForm((p) => ({ ...p, photo_url: e.target.value }))
                    }
                    placeholder="https://..."
                  />
                </div>
              </div>

              <div className="arp-actions arp-actions-bottom">
                <button className="arp-btn ghost" onClick={closeCreate}>
                  Cancel
                </button>
                <button
                  className="arp-btn primary"
                  onClick={onSaveCreate}
                  disabled={createLoading}
                >
                  {createLoading ? "Saving…" : "Create & Send Email"}
                </button>
              </div>

              <div className="arp-hint">
                User will receive a link to set password and then login.
              </div>
            </div>
          </div>
        </div>
      )}


      {/* DELETE CONFIRMATION MODAL */}
      {deleteOpen && (
        <div className="arp-backdrop" onClick={closeDelete}>
          <div
            className="arp-modal"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="arp-modal-head">
              <div className="arp-modal-title">Delete User</div>
              <button className="arp-x" onClick={closeDelete}>✕</button>
            </div>

            <div className="arp-modal-body">
              {deleteErr && <div className="arp-error">{deleteErr}</div>}

              <p style={{ marginBottom: 12 }}>
                Are you sure you want to delete{" "}
                <b>{deleteUser?.username}</b>?
              </p>

              <p style={{ color: "#b00020", fontSize: 13 }}>
                This action cannot be undone.
              </p>

              <div className="arp-actions">
                <button className="arp-btn ghost" onClick={closeDelete}>
                  Cancel
                </button>
                <button
                  className="arp-btn danger"
                  onClick={onConfirmDelete}
                  disabled={deleteLoading}
                >
                  {deleteLoading ? "Deleting…" : "Yes, Delete"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

    </div>
  );
}
